/**
 * Order Creation Hook
 * 
 * Хук для создания заказов с товарами и автоматическим определением типа заказа
 * Интегрируется с orderCreationService и обеспечивает корректное обновление кэша
 * 
 * Соблюдает STYLEGUIDE.md:
 * - React.FC типизация
 * - camelCase именование
 * - Корректная обработка ошибок
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { 
  createOrderWithItems, 
  validateOrderData,
  type CreateOrderData,
  type CreateOrderItemData 
} from '@/services/orders/orderCreationService';
import type { OrderWithDetails } from '@/types/entities/order';
import { useTaskAutoCreation, type OrderTaskData } from '@/services/tasks/taskAutoCreationService';
import { useAuth } from '@/context/AuthContext';
import { NotificationService } from '@/services/NotificationService';
import { getDisplayId } from '@/utils/displayId';

interface UseOrderCreationReturn {
  createOrder: (orderData: CreateOrderData) => Promise<OrderWithDetails>;
  isCreating: boolean;
  error: string | null;
  validateOrder: (orderData: CreateOrderData) => string[];
}

/**
 * Хук для создания заказов с товарами
 * Обеспечивает валидацию, создание и обновление кэша
 */
export const useOrderCreation = (): UseOrderCreationReturn => {
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { createTasksForOrder } = useTaskAutoCreation();

  const mutation = useMutation({
    mutationFn: async (orderData: CreateOrderData): Promise<OrderWithDetails> => {
      // Сброс предыдущих ошибок
      setError(null);

      // Валидация данных
      const validationErrors = validateOrderData(orderData);
      if (validationErrors.length > 0) {
        const errorMessage = validationErrors.join(', ');
        setError(errorMessage);
        throw new Error(errorMessage);
      }

      console.log('Creating order with items:', {
        clientId: orderData.clientId,
        itemsCount: orderData.items.length,
        totalAmount: orderData.items.reduce((sum, item) => sum + item.totalPrice, 0)
      });

      // Создание заказа
      const createdOrder = await createOrderWithItems(orderData);

      console.log('Order created successfully:', {
        orderId: createdOrder.id,
        isStorePurchase: createdOrder.isStorePurchase,
        totalAmount: createdOrder.totalAmount
      });

      return createdOrder;
    },
    onSuccess: async (createdOrder) => {
      // Обновление кэша заказов: учитываем ключ из useOrders ['orders', { userId }]
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['orders', { userId: user?.id }] });
      queryClient.invalidateQueries({ queryKey: ['orders', 'list'] });
      
      // Обновление кэша клиентов (для статистики)
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['clients', createdOrder.clientId] });
      
      // Обновление кэша статистики
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['client', 'stats'] });

      // Автоматическое создание задач (если включено в настройках)
      try {
        if (user) {
          // Получение настроек задач из системных настроек
          // TODO: Заменить на реальное получение настроек из API
          const taskSettings = {
            autoCreateTasksForOrders: true, // Временно включено для тестирования
            autoCreateTasksForRentals: true,
            defaultTaskCategory: 'order',
            defaultTaskStatus: 'pending',
          };

          // Подготовка данных для создания задач
          const orderTaskData: OrderTaskData = {
            orderId: createdOrder.id,
            clientId: createdOrder.clientId,
            clientName: createdOrder.clientName || 'Неизвестный клиент',
            orderType: createdOrder.isStorePurchase ? 'purchase' : 'rental',
            totalAmount: createdOrder.totalAmount,
            items: createdOrder.items?.map(item => ({
              name: item.name || 'Неизвестный товар',
              quantity: item.quantity,
              type: item.inventoryItemId ? 'inventory' : 'store'
            })) || []
          };

          // Создание задач
          await createTasksForOrder(orderTaskData, taskSettings);
          
          console.log('Tasks created automatically for order:', createdOrder.id);
        }
      } catch (error) {
        console.warn('Failed to create tasks for order:', error);
        // Не прерываем процесс создания заказа из-за ошибки создания задач
      }

      // Отправка уведомлений о создании заказа
      try {
        const shortDisplayId = getDisplayId(createdOrder, 6);
        await NotificationService.sendMultiChannelNotification(
          user.id,
          'Заказ создан',
          `Заказ №${shortDisplayId} успешно создан для клиента ${createdOrder.clientName}. Сумма: ${createdOrder.totalAmount}₽`,
          {
            sendEmail: false, // Можно включить при необходимости
            sendPush: true,
            pushData: {
              icon: '/icons/order-success.png',
              data: {
                orderId: createdOrder.id,
                clientId: createdOrder.clientId,
                action: 'view_order'
              }
            }
          }
        );
        
        console.log('Order creation notifications sent successfully');
      } catch (notificationError) {
        console.warn('Failed to send order creation notifications:', notificationError);
        // Не прерываем процесс из-за ошибки уведомлений
      }

      // Уведомление об успехе
      toast.success('Заказ успешно создан', {
        description: `Заказ №${getDisplayId(createdOrder, 6)} создан для клиента ${createdOrder.clientName}`
      });
    },
    onError: async (error: Error) => {
      console.error('Error creating order:', error);
      setError(error.message);
      
      // Отправка уведомления об ошибке создания заказа
      try {
        if (user) {
          await NotificationService.sendMultiChannelNotification(
            user.id,
            'Ошибка создания заказа',
            `Не удалось создать заказ: ${error.message}`,
            {
              sendEmail: false,
              sendPush: true,
              pushData: {
                icon: '/icons/error.png',
                data: {
                  error: error.message,
                  action: 'retry_order_creation'
                }
              }
            }
          );
        }
      } catch (notificationError) {
        console.warn('Failed to send error notification:', notificationError);
      }
      
      // Уведомление об ошибке
      toast.error('Ошибка создания заказа', {
        description: error.message
      });
    }
  });

  return {
    createOrder: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error,
    validateOrder: validateOrderData,
  };
};

/**
 * Хелпер для создания CreateOrderItemData из различных источников
 */
export const createOrderItem = {
  /**
   * Создает CreateOrderItemData для товара инвентаря
   */
  fromInventory: (inventoryItemId: string, quantity: number, unitPrice: number): CreateOrderItemData => ({
    inventoryItemId,
    quantity,
    unitPrice,
    totalPrice: quantity * unitPrice,
    itemType: 'inventory'
  }),

  /**
   * Создает CreateOrderItemData для товара магазина
   */
  fromStore: (storeProductId: string, quantity: number, unitPrice: number): CreateOrderItemData => ({
    storeProductId,
    quantity,
    unitPrice,
    totalPrice: quantity * unitPrice,
    itemType: 'store'
  }),

  /**
   * Создает CreateOrderItemData для услуги
   */
  fromService: (serviceId: string, quantity: number, unitPrice: number): CreateOrderItemData => ({
    serviceId,
    quantity,
    unitPrice,
    totalPrice: quantity * unitPrice,
    itemType: 'service'
  })
};

/**
 * Хелпер для расчета общей суммы заказа
 */
export const calculateOrderTotal = (items: CreateOrderItemData[]): number => {
  return items.reduce((total, item) => total + item.totalPrice, 0);
};

/**
 * Хелпер для определения типа заказа
 */
export const getOrderTypeDescription = (items: CreateOrderItemData[]): string => {
  const hasInventory = items.some(item => item.inventoryItemId);
  const hasStore = items.some(item => item.storeProductId);
  const hasServices = items.some(item => item.serviceId);

  if (hasInventory && hasStore) {
    return 'Смешанный заказ (аренда + покупка)';
  } else if (hasInventory) {
    return 'Аренда оборудования';
  } else if (hasStore) {
    return 'Покупка товаров';
  } else if (hasServices) {
    return 'Заказ услуг';
  } else {
    return 'Неопределенный тип заказа';
  }
};
